import React, { useState, useEffect, useRef } from 'react'
import {
  List,
  Datagrid,
  TextField,
  useTranslate,
  DateField,
  FunctionField,
  useListContext,
  useNotify,
  DeleteButton,
  BulkDeleteButton,
} from 'react-admin'
import { Container } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { ListViewActionsButtons } from 'src/components/uiElements/buttons/ListViewActionsButtons'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { iconAccordingProduct } from 'src/components/pages/sales/reservation/helper/IconAccordingProduct'
import { FormattedTextFieldWithStatus } from 'src/components/pages/sales/reservation/helper/FormattedTextFieldWithStatus'
import { HasPermission, hasPermissionFunction } from 'src/helper/HasPermission'
import { ITEM_PERMISSION_ENTITY, PERMISSION_EXPORT, PERMISSION_LIST, PERMISSION_SHOW, PERMISSION_SMARTBOOK } from 'src/helper/Permission'
import { DUE_DATE_RANGE_PERMISSION_ENTITY } from 'src/helper/resourcesByPermission'
import { ReservationExportButton } from './helper/ReservationExportButton'

const MyBulkActions = () => {
    const { selectedIds } = useListContext();
    if (!selectedIds || selectedIds.length === 0) return null;
    return (
      <>
        <HasPermission permission='delete' resource={'reservation'}>
          <BulkDeleteButton/>
        </HasPermission>
      </>
    );
  };

const MyDatagrid = () => {
  const translate = useTranslate()
  const { isFetching, page, filterValues } = useListContext()
  const prevPageRef = useRef()

  const prevFiltersRef = useRef(filterValues)

  const [pageChanged, setPageChanged] = useState(false)

  useEffect(() => {
    // Comprobar si la página ha cambiado y si isFetching es true
    if (prevPageRef.current !== page && isFetching) {
      prevPageRef.current = page
      setPageChanged(true)
    }
    if (prevFiltersRef.current !== filterValues && isFetching) {
      prevFiltersRef.current = filterValues
      setPageChanged(true)
    } else {
      setPageChanged(false)
    }
  }, [isFetching, page, filterValues])

  return (
    <Datagrid /* rowClick='show' */ bulkActionButtons={<MyBulkActions />} isLoading={pageChanged}>
      <DateField label={translate('ra.field.created_at')} source='create_at' />
      <TextField label={translate('ra.field.reservation_number')} source='number' />
      <TextField label={translate('ra.field.work_unit')} source='work_unit_short_name' sortable={false} />
      <FunctionField
        label={translate('ra.field.product')}
        render={(record) => iconAccordingProduct(record.items)}
      />
      <TextField label={translate('ra.field.seller')} source='user' sortable={false} />
      <TextField label={translate('ra.field.passenger')} source='passenger' sortable={false} />
      <TextField
        label={translate('ra.field.business_unit')}
        source='business_unit_long_name'
        sortable={false}
      />
      <DateField label={translate('ra.field.date_first_service')} source='date_in' />
      <FormattedTextFieldWithStatus label={translate('ra.field.status')} source='status' />

      <ListViewActionsButtons
        label={translate('ra.action.actions')}
        fieldsName={['short_name']}
        genderElement='femenine'
        disableEdit={true}
        disableDelete={true}
        disableRevision={true}
      />
    </Datagrid>
  )
}

export const ReservationList = (props) => {
  const translate = useTranslate()
  const notify = useNotify()

  const allFilters = [
    { source: 'provider_code__in', name: 'ra.field.provider_code' },
    { source: 'item__status__in', name: 'ra.field.item_status' },
    ...(hasPermissionFunction('sales', PERMISSION_LIST)
      ? [{ source: 'billing_situation', name: 'ra.field.billing_situation' }]
      : []),
    ...(hasPermissionFunction('sales', PERMISSION_LIST)
      ? [{ source: 'bases_id__in', name: 'ra.field.base_to_billing' }]
      : []),
    // ...(hasPermissionFunction('sales', PERMISSION_LIST)
    //   ? [{ source: 'billing_automatic', name: 'ra.field.billing_automatic' }]
    //   : []),
    { source: 'status__in', name: 'ra.field.reservation_status' },
    { source: 'item__payment_status', name: 'ra.field.payment_status' },
    // ...(hasPermissionFunction('sales', PERMISSION_LIST)
    //   ? [{ source: 'item_billing_status', name: 'ra.field.item_billing_status' }]
    //   : []),
    { source: 'tag__id__in', name: 'ra.field.tags' },
    { source: 'date_in', name: 'ra.field.date_first_service' },
    ...(hasPermissionFunction(DUE_DATE_RANGE_PERMISSION_ENTITY, PERMISSION_SHOW)
      ? [{ source: 'item__autocancel_date', name: 'ra.field.autocancel_date' }]
      : []),
    { source: 'create_at', name: 'ra.field.created_at' },
    { source: 'item__updated_at', name: 'ra.field.last_update_date' },
    ...(hasPermissionFunction(DUE_DATE_RANGE_PERMISSION_ENTITY, PERMISSION_SHOW)
      ? [{ source: 'invoice_date', name: 'ra.field.invoice_date' }]
      : []),
    { source: 'item__source_id__in', name: 'ra.field.source' },
    ...(hasPermissionFunction(DUE_DATE_RANGE_PERMISSION_ENTITY, PERMISSION_SHOW)
      ? [{ source: 'item__due_date_final', name: 'ra.field.due_date' }]
      : []),
    // ...(hasPermissionFunction('sales', PERMISSION_LIST)
    //   ? [{ source: 'sales_id__in', name: 'ra.field.tma_business' }]
    //   : []),
    { source: 'number__ilike_in', name: 'ra.field.reservation_number' },
    { source: 'pax', name: 'ra.field.passenger' },
    { source: 'business_unit__country_id__in', name: 'ra.field.country' },
    { source: 'item__product_id__in', name: 'ra.field.product' },
    { source: 'supplier_id__in', name: 'ra.field.supplier' },
    { source: 'promotion_code', name: 'ra.field.promotion' },
    ...(hasPermissionFunction(ITEM_PERMISSION_ENTITY, PERMISSION_SMARTBOOK)
      ? [{ source: 'item__has_smartbook', name: 'ra.field.smartbookTitle' }]
      : []),
    // { source: 'business_unit__salesperson_user_id__in', name: 'ra.field.commercial_responsible' },
    // { source: 'business_unit__user_id__in', name: 'ra.field.manager_responsible' },
    { source: 'product_type_id__in', name: 'ra.field.product_type' },
    { source: 'business_unit_id__in', name: 'ra.field.business_unit' },
    { source: 'work_unit_id__in', name: 'ra.field.work_unit' },
    { source: 'user_id__in', name: 'ra.field.seller_user' },
    { source: 'pax_item__ticket_number__in', name: 'ra.field.item_ticket_number' },
    ...(hasPermissionFunction('sales', PERMISSION_LIST)
      ? [{ source: 'item__is_billable', name: 'ra.field.is_billable' }]
      : []),
    { source: 'item__has_ancillary_hepstar', name: 'ra.field.hepstar' },
    { source: 'item_ancillary__status', name: 'ra.field.hepstar_status' },
    { source: 'item_ancillary__transaction_code__in', name: 'ra.field.hepstar_code' },
    { source: 'item_ancillary__product_name', name: 'ra.field.hepstar_product' },
    { source: 'pax_item__ticket_number__in', name: 'ra.field.item_ticket_number' },
    { source: 'item__has_service_package', name: 'ra.title.servicePackage' },
    { source: 'item_ancillary_status', name: 'ra.field.status_hepstar' },
    { source: 'item_ancillary_transaction_code', name: 'ra.field.hepstar_code' },
    { source: 'item_ancillary_product_name', name: 'ra.field.hepstar_product' },
    { source: 'item__has_service_package', name: 'ra.title.servicePackage' },
  ]

  const onSettled = (data) => {
    if (data.pageInfo) {
      notify(data.pageInfo, { type: 'error' })
    }
  }


  return (
    <>
      <Container className='content'>
        <CardCommon title={translate('ra.entity.reservation')}>
          <List
            {...props}
            component='div'
            queryOptions={{ onSettled }}
            actions={
              <ListViewTopToolbar
                customFilter
                listFilters={allFilters}
                CustomExportButton={hasPermissionFunction('reservation', PERMISSION_EXPORT) ? ReservationExportButton : null}
                exporterDisable={true}
              />
            }
            sort={{ order: 'DESC', field: 'id' }}
            exporter={false}
          >
            <MyDatagrid />
          </List>
        </CardCommon>
      </Container>
    </>
  )
}
